import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getSubscription, type SubscriptionStatus, type SubscriptionPlan, type UserRole } from '../lib/auth-client'
import { STALE_TIMES } from '../lib/query-config'

interface SubscriptionState {
  status: SubscriptionStatus
  plan: SubscriptionPlan | null
  currentPeriodEnd: string | null
  trialEnd: string | null
  hasActiveSubscription: boolean
  isAdmin: boolean
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
  canPerformPaidActions: boolean
  /**
   * True iff subscription status is 'active' AND trialEnd is in the future.
   * NOTE: we require status==='active' (not hasActiveSubscription) because
   * `hasActiveSubscription` stays true for canceled users still within their
   * paid grace period — those users are NOT in trial anymore.
   */
  isInTrial: boolean
  /** Days remaining in trial (ceil). null when not in trial. */
  trialDaysRemaining: number | null
}

const defaultState: SubscriptionState = {
  status: 'none',
  plan: null,
  currentPeriodEnd: null,
  trialEnd: null,
  hasActiveSubscription: false,
  isAdmin: false,
  isLoading: true,
  error: null,
  refetch: async () => {},
  canPerformPaidActions: false,
  isInTrial: false,
  trialDaysRemaining: null,
}

const SubscriptionContext = createContext<SubscriptionState>(defaultState)

export function useSubscription() {
  return useContext(SubscriptionContext)
}

const SUBSCRIPTION_QUERY_KEY = ['subscription'] as const

interface SubscriptionProviderProps {
  children: ReactNode
  userRole: UserRole
}

export function SubscriptionProvider({ children, userRole }: SubscriptionProviderProps) {
  const queryClient = useQueryClient()
  const isAdmin = userRole === 'ADMIN'

  const { data, isLoading, error } = useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: getSubscription,
    enabled: true,
    staleTime: STALE_TIMES.lists,
    retry: 1,
  })

  const subscription = data?.subscription
  const hasActiveSubscription = subscription?.hasActiveSubscription ?? false

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY })
  }, [queryClient])

  const status = subscription?.status ?? 'none'
  const plan = subscription?.plan ?? null
  const currentPeriodEnd = subscription?.currentPeriodEnd ?? null
  const trialEnd = subscription?.trialEnd ?? null
  const errorMessage = error instanceof Error ? error.message : null

  const { isInTrial, trialDaysRemaining } = useMemo(() => {
    if (status !== 'active' || !trialEnd) {
      return { isInTrial: false, trialDaysRemaining: null as number | null }
    }
    const trialEndMs = new Date(trialEnd).getTime()
    if (!Number.isFinite(trialEndMs) || trialEndMs <= Date.now()) {
      return { isInTrial: false, trialDaysRemaining: null as number | null }
    }
    const days = Math.max(0, Math.ceil((trialEndMs - Date.now()) / 86_400_000))
    return { isInTrial: true, trialDaysRemaining: days }
  }, [status, trialEnd])

  const value: SubscriptionState = useMemo(() => ({
    status,
    plan,
    currentPeriodEnd,
    trialEnd,
    hasActiveSubscription: hasActiveSubscription || isAdmin,
    isAdmin,
    isLoading,
    error: errorMessage,
    refetch,
    canPerformPaidActions: hasActiveSubscription || isAdmin,
    isInTrial,
    trialDaysRemaining,
  }), [status, plan, currentPeriodEnd, trialEnd, hasActiveSubscription, isAdmin, isLoading, errorMessage, refetch, isInTrial, trialDaysRemaining])

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}
